/**
 * Centralized configuration for the HRx Investigation Consent Service
 * 
 * This file contains all configuration constants, API endpoints, validation messages,
 * and environment-specific settings to avoid hardcoded values throughout the application.
 */

// Environment configuration
export const ENVIRONMENT_CONFIG = {
  development: {
    apiBaseUrl: 'http://localhost:4321',
    enableLogging: true,
    enableMetrics: true
  },
  beta: {
    apiBaseUrl: 'https://consent.beta.hrsc.pxt.amazon.dev',
    enableLogging: true,
    enableMetrics: true
  },
  prod: {
    apiBaseUrl: 'https://consent.hrsc.pxt.amazon.dev',
    enableLogging: false,
    enableMetrics: true
  }
} as const;

// Get current environment
export const getCurrentEnvironment = (): keyof typeof ENVIRONMENT_CONFIG => {
  const hostname = window.location.hostname;
  
  // Handle null, undefined, or empty hostname
  if (!hostname) {
    return 'beta';
  }
  
  if (hostname === 'localhost' || hostname === '4321' ||hostname==='http://localhost:4321') {
    return 'development';
  } else if (hostname.toLowerCase().includes('beta')) {
    return 'beta';
  } else {
    return 'prod';
  }
};

// Get current environment config
export const getEnvironmentConfig = () => {
  return ENVIRONMENT_CONFIG[getCurrentEnvironment()];
};

// API endpoints
export const API_ENDPOINTS = {
  UPDATE_CUSTOM_OBJECT: '/api/hrxInvestigationConsentBackend/service/HRxInvestigationConsentBackend/operation/UpdateCustomObject',
  VALIDATE_TOKEN: '/api/hrxInvestigationConsentBackend/service/HRxInvestigationConsentBackend/operation/ValidateGuestToken'

} as const;

// Consent configuration
export const CONSENT_CONFIG = {
  REQUIRED_TEXT: "I Consent",
  DEFAULT_REQUESTER_ID: 'consent_int_user',
  USE_CASE: 'HRSC-Investigations',
  ENTITY_NAME: 'INTERVIEW',
  REQUESTER_TYPE_AGENT: 'AGENT',
  REQUESTER_TYPE_SYSTEM_USER: 'SYSTEM_USER',
  CONSENT_PORTAL_SOURCE: 'CONSENT_PORTAL'
} as const;

// Validation messages
export const VALIDATION_MESSAGES = {
  NO_SELECTION: 'Please select a consent option before submitting.',
  INVALID_CONSENT_TEXT: (requiredText: string) => `Please type exactly: "${requiredText}"`,
  SERVICE_NOT_INITIALIZED: 'Service not initialized. Please refresh the page.',
  TOKEN_NOT_PROVIDED: 'No access token provided. Please use the link provided by your investigator.',
  TOKEN_INVALID_OR_EXPIRED: 'Invalid or expired access token.',
  TOKEN_VALIDATION_ERROR: 'Error validating access token. Please try again or contact your investigator.',
  INITIALIZATION_ERROR_AUTH: 'Error initializing authenticated session.',
  INITIALIZATION_ERROR_GUEST: 'Error validating access token. Please try again or contact your investigator.',
  SUBMISSION_ERROR: 'Error submitting consent. Please try again.',
  CUSTOM_OBJECT_UPDATE_FAILED: 'Failed to update custom object',
  NETWORK_ERROR: 'Network error occurred. Please check your connection and try again.',
  UNAUTHORIZED_ACCESS: 'Unauthorized access. Please contact your investigator for a new access link.'
} as const;

// Loading states
export enum LoadingState {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error'
}

// Monitoring configuration
export const MONITORING_CONFIG = {
  SITE: 'USAmazon',
  SERVICE_NAME: 'HRxInvestigationConsentService',
  METRICS: {
    GUEST_PAGE_LOAD: 'GuestPageLoad',
    AUTH_PAGE_LOAD: 'AuthPageLoad',
    GUEST_UNAUTHORIZED_ACCESS: 'GuestUnauthorizedAccess',
    CONSENT_ACCEPTED: 'ConsentAccepted',
    CONSENT_DECLINED: 'ConsentDeclined'
  }
} as const;

// HTTP status codes
export const HTTP_STATUS = {
  OK: 200,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503
} as const;

// Retry configuration
export const RETRY_CONFIG = {
  MAX_RETRIES: 3,
  INITIAL_DELAY: 1000,
  BACKOFF_MULTIPLIER: 2,
  MAX_DELAY: 10000
} as const;

export default {
  ENVIRONMENT_CONFIG,
  getCurrentEnvironment,
  getEnvironmentConfig,
  API_ENDPOINTS,
  CONSENT_CONFIG,
  VALIDATION_MESSAGES,
  LoadingState,
  MONITORING_CONFIG,
  HTTP_STATUS,
  RETRY_CONFIG
};
